import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// --- Reusable Components ---
const FormInput = ({ id, label, type = 'text', placeholder, value, onChange, ...props }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
    <input 
      type={type} id={id} name={id} onChange={onChange} value={value} 
      required placeholder={placeholder} 
      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-slate-700 dark:text-white" 
      {...props}
    />
  </div>
);

const FileInput = ({ id, label, accept, onChange, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input 
          type="file" id={id} name={id} onChange={onChange} required accept={accept}
          className="w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 dark:file:bg-orange-900/50 file:text-orange-700 dark:file:text-orange-200 hover:file:bg-orange-100 dark:hover:file:bg-orange-900" 
          {...props}
        />
    </div>
);

const SelectInput = ({ id, label, value, onChange, options, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <select onChange={onChange} value={value} id={id} name={id} required className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-slate-700 dark:text-white" {...props}>
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    </div>
);

const TextAreaInput = ({ id, label, placeholder, value, onChange, rows = 4 }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <textarea
        id={id} name={id} onChange={onChange} value={value}
        required placeholder={placeholder} rows={rows}
        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-slate-700 dark:text-white"
      />
    </div>
);

const RadioGroupInput = ({ label, name, value, onChange, options }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {options.map(opt => (
                <label key={opt.value} className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${value === opt.value ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
                    <input
                        type="radio"
                        name={name}
                        value={opt.value}
                        checked={value === opt.value}
                        onChange={onChange}
                        className="h-4 w-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                    />
                    <span className="ml-3 text-sm font-medium">{opt.label}</span>
                </label>
            ))}
        </div>
    </div>
);

// --- Secure File Upload Helper ---
async function uploadFileToS3(file, token) {
  const getUrlResponse = await fetch(`${process.env.REACT_APP_API_URL}/onboarding/generate-presigned-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify({ fileName: file.name, fileType: file.type })
  });
  if (!getUrlResponse.ok) throw new Error('Could not get an upload URL from server.');
  const { presignedUrl, key } = await getUrlResponse.json();

  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });
  if (!uploadResponse.ok) throw new Error('Failed to upload file to S3.');
  
  return key;
}

// --- Step 1 Components ---
function Step1PersonalInfo({ nextStep, updateFormData, formData }) {
  const brandColor = '#e46809';
  const isFormValid = formData.gender && formData.age && formData.stateOfOrigin && formData.recipientBio && formData.employmentStatus && formData.profilePhoto;

  const handleNext = (e) => { e.preventDefault(); if (isFormValid) nextStep(); };

  const employmentOptions = [
      { value: 'Employed', label: 'Employed' },
      { value: 'Self-employed', label: 'Self-employed' },
      { value: 'Unemployed', label: 'Unemployed' },
      { value: 'Student', label: 'Student' },
  ];

  return (
    <form onSubmit={handleNext} className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-3">Step 1: Personal Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SelectInput 
            id="gender" label="Gender" value={formData.gender} onChange={(e) => updateFormData('gender', e.target.value)}
            options={[{ value: "", label: "Select Gender..." }, { value: "Male", label: "Male" }, { value: "Female", label: "Female" }]}/>
        <FormInput id="age" label="Age" type="number" value={formData.age} onChange={(e) => updateFormData('age', e.target.value)} placeholder="e.g., 25" min="1" max="120" />
      </div>
      <FormInput id="stateOfOrigin" label="State of Origin" value={formData.stateOfOrigin} onChange={(e) => updateFormData('stateOfOrigin', e.target.value)} placeholder="e.g., Lagos" />
      
      <RadioGroupInput
        label="Employment Status"
        name="employmentStatus"
        value={formData.employmentStatus}
        onChange={(e) => updateFormData('employmentStatus', e.target.value)}
        options={employmentOptions}
      />
      
      <TextAreaInput 
        id="recipientBio" label="Your Bio" value={formData.recipientBio} onChange={(e) => updateFormData('recipientBio', e.target.value)}
        placeholder="Briefly tell us about yourself. This will be shown to potential donors."
      />
      <div>
        <FileInput id="profilePhoto" label="Profile Photo" accept="image/png, image/jpeg" onChange={(e) => updateFormData('profilePhoto', e.target.files[0])} />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Allowed file types: PNG, JPG.</p>
      </div>
      <div className="pt-4">
        <button type="submit" disabled={!isFormValid} className="w-full py-3 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: brandColor }}>Next Step</button>
      </div>
    </form>
  );
}

function Step1OrganizationInfo({ nextStep, updateFormData, formData }) {
  const brandColor = '#e46809';
  const currentYear = new Date().getFullYear();
  const isFormValid = formData.organizationName && formData.yearOfEstablishment && formData.stateOfOperation && formData.organizationBio && formData.profilePhoto;

  const handleNext = (e) => { e.preventDefault(); if (isFormValid) nextStep(); };

  return (
    <form onSubmit={handleNext} className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-3">Step 1: Organization Information</h3>
      
      <FormInput 
        id="organizationName" 
        label="Organization Name" 
        value={formData.organizationName} 
        onChange={(e) => updateFormData('organizationName', e.target.value)} 
        placeholder="e.g., Hope Foundation" 
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormInput 
          id="yearOfEstablishment" 
          label="Year of Establishment" 
          type="number" 
          value={formData.yearOfEstablishment} 
          onChange={(e) => updateFormData('yearOfEstablishment', e.target.value)} 
          placeholder="e.g., 2010" 
          min="1900" 
          max={currentYear}
        />
        <FormInput 
          id="stateOfOperation" 
          label="State of Operation" 
          value={formData.stateOfOperation} 
          onChange={(e) => updateFormData('stateOfOperation', e.target.value)} 
          placeholder="e.g., Lagos" 
        />
      </div>
      
      <TextAreaInput 
        id="organizationBio" 
        label="Organization Bio" 
        value={formData.organizationBio} 
        onChange={(e) => updateFormData('organizationBio', e.target.value)}
        placeholder="Tell us about your organization's mission and work. This will be shown to potential donors."
      />
      
      <div>
        <FileInput id="profilePhoto" label="Organization Logo/Photo" accept="image/png, image/jpeg" onChange={(e) => updateFormData('profilePhoto', e.target.files[0])} />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Allowed file types: PNG, JPG.</p>
      </div>
      
      <div className="pt-4">
        <button type="submit" disabled={!isFormValid} className="w-full py-3 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: brandColor }}>Next Step</button>
      </div>
    </form>
  );
}

// --- Step 2 Components ---
function Step2Verification({ prevStep, handleSubmit, updateFormData, formData, isSubmitting }) {
  const brandColor = '#e46809';
  const isFormValid = formData.idType && formData.idDocument;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-3">Step 2: Identity Verification</h3>
      
      <SelectInput 
        id="idType" 
        label="ID Type" 
        value={formData.idType} 
        onChange={(e) => updateFormData('idType', e.target.value)}
        options={[
          { value: "", label: "Select ID Type..." }, 
          { value: "NIN", label: "NIN Slip" }, 
          { value: "VotersCard", label: "Voter's Card" }, 
          { value: "DriversLicence", label: "Driver's Licence" }
        ]}
      />
      
      <div>
        <FileInput id="idDocument" label="Upload ID Document" accept="image/png, image/jpeg, application/pdf" onChange={(e) => updateFormData('idDocument', e.target.files[0])} />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Allowed file types: PNG, JPG, PDF.</p>
      </div>
      
      <div className="p-4 rounded-md bg-blue-50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-sm">
        <h4 className="font-bold">Important Notice</h4>
        <p className="mt-1">You must come in person to our designated center to receive your item. The ID you upload must match the person coming for collection.</p>
        <p className="mt-2">
          <strong>If you are a minor or do not have a valid ID:</strong> Please upload a valid ID of your parent or guardian. They must accompany you for collection and present the original ID.
        </p>
      </div>
      
      <div className="flex justify-between pt-4">
        <button type="button" onClick={prevStep} disabled={isSubmitting} className="py-3 px-6 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-gray-200 font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors disabled:opacity-75">Previous Step</button>
        <button type="button" onClick={handleSubmit} disabled={!isFormValid || isSubmitting} className="py-3 px-6 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: brandColor }}>
            {isSubmitting ? 'Submitting...' : 'Submit Profile'}
        </button>
      </div>
    </div>
  );
}

function Step2OrganizationVerification({ prevStep, handleSubmit, updateFormData, formData, isSubmitting }) {
  const brandColor = '#e46809';
  const isFormValid = formData.idType && formData.idDocument;

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 border-b border-gray-200 dark:border-gray-600 pb-3">Step 2: Organization Verification</h3>
      
      <SelectInput 
        id="idType" 
        label="Document Type" 
        value={formData.idType} 
        onChange={(e) => updateFormData('idType', e.target.value)}
        options={[
          { value: "", label: "Select Document Type..." }, 
          { value: "CAC", label: "CAC Registration Certificate" }, 
          { value: "TrustDeed", label: "Trust Deed" }, 
          { value: "Incorporation", label: "Certificate of Incorporation" }, 
          { value: "Other", label: "Other Legal Document" }
        ]}
      />
      
      <div>
        <FileInput id="idDocument" label="Upload Registration Document" accept="image/png, image/jpeg, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => updateFormData('idDocument', e.target.files[0])} />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Allowed file types: PNG, JPG, PDF, DOC, DOCX.</p>
      </div>
      
      <div className="p-4 rounded-md bg-blue-50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 text-sm">
        <h4 className="font-bold">Important Notice for Organizations</h4>
        <p className="mt-1">
          You must provide valid registration documents for your organization. The authorized representative must come in person to our designated center to receive items.
        </p>
        <p className="mt-2">
          <strong>If your organization is over 50 years old and cannot access registration documents:</strong> 
          You may upload a letter from the CEO/MD/Principal Officer (a letter of consent) along with any available supporting documentation.
        </p>
        <p className="mt-2">
          <strong>For educational institutions:</strong> Please provide your school registration certificate or a letter from the principal.
        </p>
      </div>
      
      <div className="flex justify-between pt-4">
        <button type="button" onClick={prevStep} disabled={isSubmitting} className="py-3 px-6 bg-gray-200 dark:bg-slate-600 text-gray-800 dark:text-gray-200 font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors disabled:opacity-75">Previous Step</button>
        <button type="button" onClick={handleSubmit} disabled={!isFormValid || isSubmitting} className="py-3 px-6 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ backgroundColor: brandColor }}>
            {isSubmitting ? 'Submitting...' : 'Submit Organization Profile'}
        </button>
      </div>
    </div>
  );
}

// --- Main OnboardingPage Component ---
export default function OnboardingPage() {
  const { token, fetchUserProfile, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Initialize form data based on user role
  const [formData, setFormData] = useState(() => {
    if (user.role === 'organization') {
      return {
        organizationName: '',
        yearOfEstablishment: '',
        stateOfOperation: '',
        organizationBio: '',
        profilePhoto: null,
        idType: '',
        idDocument: null,
      };
    } else {
      return {
        gender: '',
        age: '',
        stateOfOrigin: '',
        recipientBio: '',
        employmentStatus: '',
        profilePhoto: null,
        idType: '',
        idDocument: null,
      };
    }
  });

  const updateFormData = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      const [profilePhotoKey, idDocumentKey] = await Promise.all([
        uploadFileToS3(formData.profilePhoto, token),
        uploadFileToS3(formData.idDocument, token)
      ]);
      
      let finalData;
      if (user.role === 'organization') {
        finalData = {
          organizationName: formData.organizationName,
          yearOfEstablishment: parseInt(formData.yearOfEstablishment, 10),
          stateOfOperation: formData.stateOfOperation,
          organizationBio: formData.organizationBio,
          idType: formData.idType,
          profilePhotoKey,
          idDocumentKey
        };
      } else {
        finalData = {
          gender: formData.gender,
          age: parseInt(formData.age, 10),
          stateOfOrigin: formData.stateOfOrigin,
          recipientBio: formData.recipientBio,
          employmentStatus: formData.employmentStatus,
          idType: formData.idType,
          profilePhotoKey,
          idDocumentKey
        };
      }

      const endpoint = user.role === 'organization' 
        ? `${process.env.REACT_APP_API_URL}/onboarding/organization/submit`
        : `${process.env.REACT_APP_API_URL}/onboarding/recipient/submit`;

      const submitResponse = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(finalData)
      });
      
      if (!submitResponse.ok) {
        const errData = await submitResponse.json();
        throw new Error(errData.message || 'Failed to submit onboarding form.');
      }
      
      await fetchUserProfile(token);
      const redirectPath = user.role === 'organization' ? '/organization/dashboard' : '/recipient/dashboard';
      navigate(redirectPath);
    } catch (err) {
      console.error("Onboarding failed:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const progress = step === 1 ? '50%' : '100%';

  const renderStep = () => {
    if (user.role === 'organization') {
      switch (step) {
        case 1:
          return <Step1OrganizationInfo nextStep={nextStep} updateFormData={updateFormData} formData={formData} />;
        case 2:
          return <Step2OrganizationVerification prevStep={prevStep} handleSubmit={handleSubmit} updateFormData={updateFormData} formData={formData} isSubmitting={isSubmitting} />;
        default:
          return null;
      }
    } else {
      switch (step) {
        case 1:
          return <Step1PersonalInfo nextStep={nextStep} updateFormData={updateFormData} formData={formData} />;
        case 2:
          return <Step2Verification prevStep={prevStep} handleSubmit={handleSubmit} updateFormData={updateFormData} formData={formData} isSubmitting={isSubmitting} />;
        default:
          return null;
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-2xl">
        <div className="p-8 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl font-extrabold text-center text-gray-900 dark:text-white">
              {user.role === 'organization' ? 'Complete Your Organization Profile' : 'Complete Your Profile'}
            </h1>
            <div className="mt-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div className="bg-orange-500 h-2.5 rounded-full transition-all duration-500" style={{ width: progress }}></div>
            </div>
        </div>
        <div className="p-8">
          {error && <p className="text-center text-red-500 font-semibold mb-4">{error}</p>}
          {renderStep()}
        </div>
      </div>
    </div>
  );
}